import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

function cellDisplayValue(cell) {
  if (!cell || cell.value === undefined || cell.value === null) return '';
  const v = cell.value;
  if (v instanceof Date) return v;
  if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'string') return v;
  // An error cell (#N/A, #REF!, ...) carries no value; read it as blank rather than as its error text
  if (typeof v === 'object' && v.error !== undefined) return '';
  if (cell.text != null && String(cell.text) !== '') return String(cell.text);
  if (typeof v === 'object' && v.formula !== undefined) {
    const r = v.result;
    if (r instanceof Date) return r;
    if (r !== null && r !== undefined && typeof r !== 'object') return r;
    return String(cell.text || '');
  }
  if (typeof v === 'object' && v.richText) return String(cell.text || '');
  return '';
}

/**
 * First worksheet → objects keyed by row-1 header strings (like XLSX.utils.sheet_to_json, blank rows omitted).
 * @param {ArrayBuffer} arrayBuffer
 * @returns {Promise<Record<string, unknown>[]>}
 */
export async function parseXlsxFirstSheetToObjects(arrayBuffer) {
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(arrayBuffer);
  const ws = wb.worksheets[0];
  if (!ws) return [];

  const headerRow = ws.getRow(1);
  /** @type {Record<number, string>} */
  const headers = {};
  let maxCol = 0;
  headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const h = cellDisplayValue(cell);
    headers[colNumber] = h === undefined || h === null ? '' : String(h);
    maxCol = Math.max(maxCol, colNumber);
  });
  if (maxCol === 0) return [];

  const lastRow = ws.lastRow ? ws.lastRow.number : 1;
  const out = [];
  for (let r = 2; r <= lastRow; r++) {
    const obj = {};
    const row = ws.getRow(r);
    for (let c = 1; c <= maxCol; c++) {
      const key = headers[c];
      if (key === '') continue;
      const val = cellDisplayValue(row.getCell(c));
      obj[key] = val === undefined || val === null ? '' : val;
    }
    const empty =
      Object.keys(obj).length === 0 ||
      Object.values(obj).every((v) => String(v ?? '').trim() === '');
    if (!empty) out.push(obj);
  }
  return out;
}

/** "First Name *", "first  name", "FIRST NAME:" and "First-Name" all become "first name". */
export const normalizeHeader = (h) => String(h ?? '')
  .replace(/\([^)]*\)/g, ' ')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

/**
 * Renames spreadsheet columns to internal keys. Headers are matched loosely (case, spacing,
 * "*" / ":" markers and punctuation are ignored), so a sheet whose headings were retyped as
 * "First name" or "EMAIL" still maps — an unmatched heading used to drop that whole column and
 * reject the row as "Missing required fields" even though the value was there.
 * @param {Record<string, unknown>[]} rows      rows keyed by the sheet's header text
 * @param {Record<string, string>} keyMap        exact template header -> internal key
 * @param {Record<string, string>} [aliases]     other common headings -> internal key
 * @returns {{ rows: Record<string, unknown>[], unmatched: string[] }}
 */
export function mapImportHeaders(rows, keyMap, aliases = {}) {
  const lookup = new Map();
  for (const [header, key] of Object.entries({ ...aliases, ...keyMap })) lookup.set(normalizeHeader(header), key);
  const unmatched = new Set();
  const mapped = rows.map((row) => {
    const out = {};
    for (const [header, value] of Object.entries(row)) {
      const key = lookup.get(normalizeHeader(header));
      if (key) {
        // Two columns mapping to one key (e.g. "Phone" and "WhatsApp"): keep the first non-blank value
        if (out[key] === undefined || String(out[key]).trim() === '') out[key] = value;
      } else {
        if (String(header).trim()) unmatched.add(String(header).trim());
        out[header] = value;
      }
    }
    return out;
  });
  return { rows: mapped, unmatched: [...unmatched] };
}

/** Common alternative headings people type instead of the template's. */
export const IMPORT_HEADER_ALIASES = {
  'E-mail': 'email', 'Email Address': 'email', 'Mail': 'email',
  'NIC No': 'nic', 'NIC Number': 'nic', 'National ID': 'nic', 'NIC Passport': 'nic', 'NIC / Passport': 'nic',
  'Surname': 'lastName', 'Family Name': 'lastName', 'Given Name': 'firstName', 'Forename': 'firstName',
  'DOB': 'dateOfBirth', 'Birthday': 'dateOfBirth', 'Birth Date': 'dateOfBirth', 'Date Of Birth': 'dateOfBirth',
  'Mobile': 'mobilePhone', 'Mobile No': 'mobilePhone', 'Mobile Number': 'mobilePhone',
  'Membership Category': 'membershipType', 'Member Type': 'membershipType', 'Membership': 'membershipType', 'Category': 'membershipType',
  'Address': 'address.line1', 'Address 1': 'address.line1', 'Address 2': 'address.line2', 'Address 3': 'address.line3',
  'Town City': 'address.city', 'Postcode': 'address.postalCode', 'Zip Code': 'address.postalCode',
  'Sex': 'gender', 'Salutation': 'title',
};

/**
 * Export a 2D array of values to .xlsx (like XLSX.utils.aoa_to_sheet + writeFile).
 * @param {unknown[][]} rows2d
 * @param {string} sheetName
 * @param {string} filename
 * @param {{ wch: number }[] | undefined} colWchs
 * @param {{ numberFormat?: string, numberColumns?: number[] }} [options] numberFormat (e.g. '#,##0.00')
 *   is applied to numeric cells of the given 1-based columns
 */
export async function downloadXlsxFromAoa(rows2d, sheetName, filename, colWchs, options = {}) {
  const wb = new ExcelJS.Workbook();
  const sheet = wb.addWorksheet(sheetName);
  rows2d.forEach((row) => {
    if (Array.isArray(row)) sheet.addRow(row);
    else sheet.addRow([]);
  });
  if (colWchs && colWchs.length) {
    colWchs.forEach((spec, i) => {
      if (spec && spec.wch != null) sheet.getColumn(i + 1).width = spec.wch;
    });
  }
  if (options.numberFormat && options.numberColumns) {
    options.numberColumns.forEach((col) => {
      sheet.getColumn(col).eachCell((cell) => {
        if (typeof cell.value === 'number') cell.numFmt = options.numberFormat;
      });
    });
  }
  const buf = await wb.xlsx.writeBuffer();
  saveAs(
    new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    filename
  );
}

/**
 * 'yyyy-mm-dd' / 'dd/mm/yyyy' / 'dd-mm-yyyy' strings (or a Date) -> Date, else null.
 * Built with Date.UTC (not the local-timezone constructor) so the calendar day is preserved
 * once ExcelJS/JSON serialize it to UTC — otherwise a timezone ahead of UTC (e.g. Colombo,
 * +5:30) would shift local midnight back onto the previous day.
 */
function toDateOrNull(value) {
  if (value instanceof Date) return value;
  if (typeof value !== 'string' || !value.trim()) return null;
  const s = value.trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (iso) return new Date(Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])));
  const dmy = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(s);
  if (dmy) return new Date(Date.UTC(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1])));
  return null;
}

/**
 * Export JSON rows (objects with same keys) to .xlsx (like json_to_sheet + writeFile).
 * @param {Record<string, unknown>[]} rows
 * @param {string} sheetName
 * @param {string} filename
 * @param {string[]} mandatoryFields - header names that must be filled in; highlighted green with a "*" suffix
 * @param {Record<string, string[]>} dropdowns - header name -> allowed values, rendered as an in-cell dropdown list
 * @param {string[]} dateColumns - header names that hold dates; formatted as dates with a calendar-icon picker and date validation
 */
export async function downloadXlsxFromJsonRows(rows, sheetName, filename, mandatoryFields = [], dropdowns = {}, dateColumns = []) {
  const wb = new ExcelJS.Workbook();
  const sheet = wb.addWorksheet(sheetName);
  if (!rows.length) {
    const buf = await wb.xlsx.writeBuffer();
    saveAs(
      new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      filename
    );
    return;
  }
  const keys = Object.keys(rows[0]);
  // Header text is left exactly as-is (no "*" suffix): re-uploaded files are matched back to
  // their internal field by this exact string, so decorating it here would break that lookup.
  // Mandatory columns are still unambiguous via the green fill, the hover note, and the
  // Instructions sheet below.
  const headerRow = sheet.addRow(keys);

  headerRow.eachCell((cell, colNumber) => {
    cell.font = { bold: true };
    const fieldName = keys[colNumber - 1];
    if (mandatoryFields.includes(fieldName)) {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFC6EFCE' } // Light green background
      };
      cell.font = { bold: true, color: { argb: 'FF006100' } }; // Dark green text
      cell.note = 'Mandatory field — this column must be filled in for every row.';
    }
  });

  // Data rows are added BEFORE the per-cell validation loops below. ExcelJS's addRow() always
  // appends after the sheet's current rowCount, and touching a not-yet-existing cell (e.g. row 500)
  // via getCell() silently extends rowCount to that row — so looping getCell() over rows 2..1000
  // first would push these real sample rows all the way down past row 1000 instead of row 2.
  rows.forEach((obj) => {
    sheet.addRow(keys.map((k) => {
      const v = obj[k];
      if (dateColumns.includes(k)) {
        const d = toDateOrNull(v);
        if (d) return d;
      }
      return v === undefined || v === null ? '' : v;
    }));
  });
  keys.forEach((_, i) => {
    sheet.getColumn(i + 1).width = Math.max(18, keys[i].length + 2);
  });

  if (dropdowns && Object.keys(dropdowns).length > 0) {
    Object.keys(dropdowns).forEach(colName => {
      const colIndex = keys.indexOf(colName);
      if (colIndex >= 0) {
        const letter = sheet.getColumn(colIndex + 1).letter;
        const formulaStr = `"${dropdowns[colName].join(',')}"`;
        // Apply to rows 2 to 1000 individually to avoid 'marked' undefined bug in exceljs ranges
        for (let r = 2; r <= 1000; r++) {
          sheet.getCell(`${letter}${r}`).dataValidation = {
            type: 'list',
            allowBlank: true,
            showErrorMessage: true,
            errorStyle: 'error',
            errorTitle: 'Invalid Selection',
            error: 'Please select a value from the list.',
            formulae: [formulaStr]
          };
        }
      }
    });
  }

  if (dateColumns && dateColumns.length > 0) {
    dateColumns.forEach((colName) => {
      const colIndex = keys.indexOf(colName);
      if (colIndex < 0) return;
      const letter = sheet.getColumn(colIndex + 1).letter;
      // Applied per-cell (not as a range) for the same reason as the dropdown validation above.
      for (let r = 2; r <= 1000; r++) {
        const cell = sheet.getCell(`${letter}${r}`);
        cell.numFmt = 'dd/mm/yyyy';
        cell.dataValidation = {
          type: 'date',
          operator: 'between',
          allowBlank: true,
          showErrorMessage: true,
          errorStyle: 'error',
          errorTitle: 'Invalid Date',
          error: 'Enter a valid date (dd/mm/yyyy), or click the cell and use the calendar icon that appears.',
          formulae: [new Date(1900, 0, 1), new Date(2100, 11, 31)]
        };
      }
    });
  }

  if (mandatoryFields.length || Object.keys(dropdowns).length || dateColumns.length) {
    const info = wb.addWorksheet('Instructions');
    info.getColumn(1).width = 100;
    const addLine = (text, opts = {}) => {
      const row = info.addRow([text]);
      if (opts.bold) row.getCell(1).font = { bold: true };
      row.getCell(1).alignment = { wrapText: true };
      return row;
    };
    addLine('How to fill in this template', { bold: true });
    addLine('');
    if (mandatoryFields.length) {
      addLine('Columns highlighted in green (hover the header for a tooltip) are mandatory and must be filled in for every row.', { bold: false });
      addLine(`Mandatory columns: ${mandatoryFields.join(', ')}`);
      addLine('');
    }
    if (Object.keys(dropdowns).length) {
      addLine('Columns below only accept a value picked from a dropdown list. Click the cell and use the arrow that appears to choose a value.');
      Object.keys(dropdowns).forEach((col) => addLine(`  ${col}: ${dropdowns[col].join(', ')}`));
      addLine('');
    }
    if (dateColumns.length) {
      addLine(`Date columns (${dateColumns.join(', ')}) are formatted as dates. Click the cell and use the calendar icon that appears to pick a date, or type it as dd/mm/yyyy.`);
    }
  }

  const buf = await wb.xlsx.writeBuffer();
  saveAs(
    new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    filename
  );
}
